"use client";

import * as React from "react";
import { useTranslator } from "~/i18n/provider";

/**
 * P5-16 · <StarRating> (P5-DB03 §02). One display variant used everywhere the
 * aggregate appears: the NUMBER is authoritative; the glyph row rounds to the
 * nearest half and renders the half via a 50%-clipped overlay. Value text
 * always accompanies the glyphs (never colour/shape alone).
 */
const SIZES = { sm: "text-xs", md: "text-lg", lg: "text-2xl" } as const;

function Star({ fill }: { fill: "full" | "half" | "empty" }) {
  if (fill === "half") {
    return (
      <span className="relative inline-block text-border" aria-hidden="true">
        ★
        <span className="absolute inset-y-0 left-0 w-1/2 overflow-hidden text-warning">
          ★
        </span>
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={fill === "full" ? "text-warning" : "text-border"}
    >
      ★
    </span>
  );
}

export function StarRating({
  value,
  size = "md",
  className,
}: {
  /** 0–5; rendered to the nearest half star. */
  value: number;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const half = Math.round(value * 2) / 2;
  return (
    <span
      role="img"
      aria-label={`Rated ${value.toFixed(1)} out of 5`}
      className={`inline-flex gap-0.5 leading-none ${SIZES[size]} ${className ?? ""}`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          fill={half >= i ? "full" : half >= i - 0.5 ? "half" : "empty"}
        />
      ))}
    </span>
  );
}

/**
 * Input variant: a radiogroup operable by pointer AND keyboard (arrow keys
 * move the value; each star is a real radio). Announced as "N of 5 stars".
 */
export function StarRatingInput({
  value,
  onChange,
  invalid,
  describedBy,
}: {
  value: number | null;
  onChange: (v: number) => void;
  invalid?: boolean;
  describedBy?: string;
}) {
  const t = useTranslator();
  return (
    <div
      role="radiogroup"
      aria-label={t("reviews.yourRating")}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className="flex gap-1"
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const active = value !== null && star <= value;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} of 5 stars`}
            onClick={() => onChange(star)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowUp") {
                e.preventDefault();
                onChange(Math.min(5, (value ?? 0) + 1));
              } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
                e.preventDefault();
                onChange(Math.max(1, (value ?? 2) - 1));
              }
            }}
            className={`text-2xl leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${active ? "text-warning" : "text-border hover:text-warning/60"}`}
          >
            <span aria-hidden="true">★</span>
          </button>
        );
      })}
    </div>
  );
}
